import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useFormatting } from '../../context/FormattingContext';
import { useAccounts } from '../../hooks/useAccounts';
import { Notification, NotificationPreferences, NotificationType } from '../../types/Notification';
import {
  formatDateInTimeZone,
  getMonthBoundsDateKeysInTimeZone,
} from '../../utils/formatting';
import {
  getUnreadNotifications,
  invalidateUnreadNotifications,
} from '../../lib/notificationsUnreadCache';
import { authFetch } from '../../lib/authFetch';

interface NotificationsPanelProps {
  range?: { startDate: string; endDate: string } | null;
  variant?: 'preview' | 'full';
  limit?: number;
  hidePreferences?: boolean;
  onNotificationsChanged?: () => void;
}

const clonePrefs = (prefs: NotificationPreferences): NotificationPreferences =>
  JSON.parse(JSON.stringify(prefs)) as NotificationPreferences;

const DEFAULT_CREDIT_UTILIZATION_WARN_PCT = 70;
const DEFAULT_CREDIT_UTILIZATION_CRITICAL_PCT = 85;

const normalizePrefs = (prefs: NotificationPreferences): NotificationPreferences => ({
  ...prefs,
  account: {
    ...prefs.account,
    creditUtilizationWarnPct:
      typeof prefs.account?.creditUtilizationWarnPct === 'number' &&
      Number.isFinite(prefs.account.creditUtilizationWarnPct)
        ? prefs.account.creditUtilizationWarnPct
        : DEFAULT_CREDIT_UTILIZATION_WARN_PCT,
    creditUtilizationCriticalPct:
      typeof prefs.account?.creditUtilizationCriticalPct === 'number' &&
      Number.isFinite(prefs.account.creditUtilizationCriticalPct)
        ? prefs.account.creditUtilizationCriticalPct
        : DEFAULT_CREDIT_UTILIZATION_CRITICAL_PCT,
  },
  delivery: {
    email: {
      enabled: Boolean(prefs.delivery?.email?.enabled),
    },
  },
});

const formatTypeLabel = (type: string): string =>
  type
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .split('_')
    .flatMap((part) => part.split(' '))
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const formatFactValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const abbreviateId = (value?: string) => {
  if (!value) return '';
  if (value.length <= 12) return value;
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
};

const dispatchNotificationsChanged = () => {
  window.dispatchEvent(new Event('notifications:changed'));
};

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  range,
  variant = 'full',
  limit = 3,
  hidePreferences = false,
  onNotificationsChanged,
}) => {
  const { auth } = useAuth();
  const { accounts } = useAccounts();
  const formatting = useFormatting();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedWhyIds, setExpandedWhyIds] = useState<Record<string, boolean>>({});
  const [fullFilter, setFullFilter] = useState<'unread' | 'all'>('unread');

  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsLoadError, setPrefsLoadError] = useState<string | null>(null);
  const [prefsSaveError, setPrefsSaveError] = useState<string | null>(null);
  const [prefsSaveSuccess, setPrefsSaveSuccess] = useState<string | null>(null);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [prefsDirty, setPrefsDirty] = useState(false);
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [draftPreferences, setDraftPreferences] = useState<NotificationPreferences | null>(null);
  const [disabledCategoriesInput, setDisabledCategoriesInput] = useState('');
  const notificationsRequestSeqRef = useRef(0);

  const effectiveRange = useMemo(() => {
    if (range) return range;
    const { startDateKey, endDateKey } = getMonthBoundsDateKeysInTimeZone(formatting.timeZone);
    return { startDate: startDateKey, endDate: endDateKey };
  }, [range, formatting.timeZone]);
  const isPreview = variant === 'preview';
  const shouldShowPreferences = !isPreview && !hidePreferences;

  const formatNotificationDate = (value?: string) => {
    if (!value) return '';
    const rendered = formatDateInTimeZone(value, {
      locale: formatting.locale,
      timeZone: formatting.timeZone,
      dateStyle: 'medium',
    });
    return rendered === '—' ? value : rendered;
  };

  const fetchNotifications = async (options: { force?: boolean } = {}) => {
    if (!auth.token) return;

    const requestId = notificationsRequestSeqRef.current + 1;
    notificationsRequestSeqRef.current = requestId;
    setLoading(true);
    try {
      const unreadOnly = isPreview || fullFilter === 'unread';
      let data: Notification[];

      if (!isPreview && unreadOnly) {
        data = await getUnreadNotifications({ force: options.force });
      } else {
        const endpoint = unreadOnly ? '/api/notifications?unreadOnly=true' : '/api/notifications';
        const res = await authFetch(endpoint, {
          headers: { Authorization: `Bearer ${auth.token}` },
        });
        const payload = await res.json();
        data = Array.isArray(payload) ? payload : [];
      }

      if (requestId !== notificationsRequestSeqRef.current) return;
      setNotifications(data);
    } catch (err) {
      if (requestId !== notificationsRequestSeqRef.current) return;
      console.error('Notifications fetch error:', err);
    } finally {
      if (requestId === notificationsRequestSeqRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchPreferences = async () => {
    if (!auth.token) return;

    setPrefsLoading(true);
    setPrefsLoadError(null);
    try {
      const res = await authFetch('/api/notifications/preferences', {
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({} as { message?: string }));
        throw new Error(data.message || 'Failed to load preferences');
      }
      const data = normalizePrefs((await res.json()) as NotificationPreferences);
      setPreferences(data);
      setDraftPreferences(clonePrefs(data));
      setDisabledCategoriesInput((data.category.disabledCategories || []).join(', '));
      setPrefsLoaded(true);
      setPrefsDirty(false);
      setPrefsSaveError(null);
      setPrefsSaveSuccess(null);
    } catch (err) {
      setPrefsLoadError(err instanceof Error ? err.message : 'Failed to load preferences');
    } finally {
      setPrefsLoading(false);
    }
  };

  const handleTogglePreferences = async () => {
    const next = !prefsOpen;
    setPrefsOpen(next);
    if (next && !prefsLoaded) {
      await fetchPreferences();
    }
  };

  const notifyChanged = () => {
    invalidateUnreadNotifications();
    onNotificationsChanged?.();
    dispatchNotificationsChanged();
  };

  const markRead = async (id: string) => {
    if (!auth.token) return;

    try {
      const res = await authFetch(`/api/notifications/${id}/read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to mark read');
      }

      if (isPreview || fullFilter === 'unread') {
        setNotifications((prev) => prev.filter((n) => n._id !== id));
      } else {
        setNotifications((prev) =>
          prev.map((n) => (n._id === id ? { ...n, readAt: n.readAt || new Date().toISOString() } : n))
        );
      }

      notifyChanged();
    } catch (err) {
      console.error('Mark read error:', err);
    }
  };

  const evaluate = async () => {
    if (!auth.token) return;

    try {
      const res = await authFetch('/api/notifications/evaluate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify({
          startDate: effectiveRange.startDate,
          endDate: effectiveRange.endDate,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to evaluate');
      }
      invalidateUnreadNotifications();
      await fetchNotifications({ force: true });
      notifyChanged();
    } catch (err) {
      console.error('Evaluate error:', err);
    }
  };

  const updateDraft = (updater: (prev: NotificationPreferences) => NotificationPreferences) => {
    setDraftPreferences((prev) => {
      if (!prev) return prev;
      const next = updater(clonePrefs(prev));
      setPrefsDirty(true);
      setPrefsSaveSuccess(null);
      return next;
    });
  };

  const toggleAccountDisabled = (accountId: string, disabled: boolean) => {
    updateDraft((prev) => {
      const set = new Set(prev.account.disabledAccountIds || []);
      if (disabled) set.add(accountId);
      else set.delete(accountId);
      prev.account.disabledAccountIds = Array.from(set);
      return prev;
    });
  };

  const toggleAccountThresholdEnabled = (accountId: string, enabled: boolean) => {
    updateDraft((prev) => {
      const thresholds = [...(prev.account.lowBalanceThresholds || [])];
      const idx = thresholds.findIndex((item) => item.accountId === accountId);
      if (idx >= 0) {
        thresholds[idx].enabled = enabled;
      } else {
        thresholds.push({ accountId, threshold: 0, enabled });
      }
      prev.account.lowBalanceThresholds = thresholds;
      return prev;
    });
  };

  const updateAccountThresholdValue = (accountId: string, value: string) => {
    const parsed = Number(value);
    updateDraft((prev) => {
      const thresholds = [...(prev.account.lowBalanceThresholds || [])];
      const idx = thresholds.findIndex((item) => item.accountId === accountId);
      if (idx >= 0) {
        thresholds[idx].threshold = Number.isFinite(parsed) ? parsed : thresholds[idx].threshold;
      } else {
        thresholds.push({
          accountId,
          enabled: true,
          threshold: Number.isFinite(parsed) ? parsed : 0,
        });
      }
      prev.account.lowBalanceThresholds = thresholds;
      return prev;
    });
  };

  const savePreferences = async () => {
    if (!draftPreferences || !auth.token) return;
    const warnPct = Number(draftPreferences.account.creditUtilizationWarnPct);
    const criticalPct = Number(draftPreferences.account.creditUtilizationCriticalPct);
    if (!Number.isFinite(warnPct) || !Number.isFinite(criticalPct)) {
      setPrefsSaveError('Credit utilization warn/critical thresholds must be numeric percentages.');
      return;
    }
    if (warnPct >= criticalPct) {
      setPrefsSaveError('Credit utilization warn threshold must be lower than critical threshold.');
      return;
    }

    const parsedCategories = disabledCategoriesInput
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part.length > 0);

    const payload = {
      enabled: draftPreferences.enabled,
      quietHours: {
        enabled: draftPreferences.quietHours.enabled,
        start: draftPreferences.quietHours.start,
        end: draftPreferences.quietHours.end,
        timezone: draftPreferences.quietHours.timezone,
      },
      typeToggles: draftPreferences.typeToggles,
      account: {
        disabledAccountIds: draftPreferences.account.disabledAccountIds,
        lowBalanceThresholds: draftPreferences.account.lowBalanceThresholds,
        creditUtilizationWarnPct: warnPct,
        creditUtilizationCriticalPct: criticalPct,
      },
      category: {
        disabledCategories: parsedCategories,
        unusualSpend: draftPreferences.category.unusualSpend,
      },
      roleScope: draftPreferences.roleScope,
    };

    setPrefsSaving(true);
    setPrefsSaveError(null);
    setPrefsSaveSuccess(null);
    try {
      const res = await authFetch('/api/notifications/preferences', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({} as { message?: string }));
        throw new Error(data.message || 'Failed to save preferences');
      }
      const saved = normalizePrefs((await res.json()) as NotificationPreferences);
      setPreferences(saved);
      setDraftPreferences(clonePrefs(saved));
      setDisabledCategoriesInput((saved.category.disabledCategories || []).join(', '));
      setPrefsDirty(false);
      setPrefsSaveSuccess('Preferences saved.');
    } catch (err) {
      setPrefsSaveError(err instanceof Error ? err.message : 'Failed to save preferences');
    } finally {
      setPrefsSaving(false);
    }
  };

  const cancelPreferences = () => {
    if (!preferences) return;
    setDraftPreferences(clonePrefs(preferences));
    setDisabledCategoriesInput((preferences.category.disabledCategories || []).join(', '));
    setPrefsDirty(false);
    setPrefsSaveError(null);
    setPrefsSaveSuccess(null);
  };

  const saveEmailDeliveryPreference = async (enabled: boolean) => {
    if (!auth.token) return;

    const previousEnabled = Boolean(draftPreferences?.delivery?.email?.enabled);
    setDraftPreferences((prev) =>
      prev
        ? {
            ...prev,
            delivery: {
              email: {
                enabled,
              },
            },
          }
        : prev
    );

    setPrefsSaving(true);
    setPrefsSaveError(null);
    setPrefsSaveSuccess(null);
    try {
      const res = await authFetch('/api/notifications/preferences', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify({
          delivery: {
            email: {
              enabled,
            },
          },
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({} as { message?: string }));
        throw new Error(data.message || 'Failed to save preferences');
      }

      const saved = normalizePrefs((await res.json()) as NotificationPreferences);
      setPreferences((prev) => ({
        ...(prev || saved),
        delivery: {
          email: {
            enabled: Boolean(saved.delivery?.email?.enabled),
          },
        },
      }));
      setDraftPreferences((prev) =>
        prev
          ? {
              ...prev,
              delivery: {
                email: {
                  enabled: Boolean(saved.delivery?.email?.enabled),
                },
              },
            }
          : clonePrefs(saved)
      );
      setPrefsSaveSuccess('Email delivery preference saved.');
    } catch (err) {
      setDraftPreferences((prev) =>
        prev
          ? {
              ...prev,
              delivery: {
                email: {
                  enabled: previousEnabled,
                },
              },
            }
          : prev
      );
      setPrefsSaveError(err instanceof Error ? err.message : 'Failed to save preferences');
    } finally {
      setPrefsSaving(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.token, variant, fullFilter]);

  const displayedNotifications = isPreview ? notifications.slice(0, limit) : notifications;
  const hasHiddenPreviewItems = isPreview && notifications.length > limit;

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6">Notifications</Typography>
        {!isPreview ? (
          <Button variant="outlined" size="small" onClick={evaluate}>
            Evaluate
          </Button>
        ) : null}
      </Stack>

      {!isPreview ? (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            size="small"
            variant={fullFilter === 'unread' ? 'contained' : 'outlined'}
            onClick={() => setFullFilter('unread')}
          >
            Unread
          </Button>
          <Button
            size="small"
            variant={fullFilter === 'all' ? 'contained' : 'outlined'}
            onClick={() => setFullFilter('all')}
          >
            All
          </Button>
        </Stack>
      ) : null}

      {shouldShowPreferences ? (
        <Box sx={{ mb: 2 }}>
          <Button
            size="small"
            variant="text"
            onClick={handleTogglePreferences}
            data-testid="notif-prefs-toggle"
          >
            Preferences
          </Button>
          <Collapse in={prefsOpen}>
            <Paper variant="outlined" sx={{ p: 1.5, mt: 1 }}>
              {prefsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">Loading preferences...</Typography>
                  </Stack>
                </Box>
              ) : prefsLoadError ? (
                <Stack spacing={1}>
                  <Typography variant="body2" color="error.main">{prefsLoadError}</Typography>
                  <Button size="small" variant="outlined" onClick={fetchPreferences}>Retry</Button>
                </Stack>
              ) : draftPreferences ? (
                <Stack spacing={1.25}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={draftPreferences.enabled}
                        onChange={(e) =>
                          updateDraft((prev) => {
                            const nextEnabled = e.target.checked;
                            if (nextEnabled) {
                              return { ...prev, enabled: true };
                            }

                            const allTypesOff = Object.fromEntries(
                              Object.keys(prev.typeToggles).map((type) => [type, false])
                            ) as Record<NotificationType, boolean>;

                            return {
                              ...prev,
                              enabled: false,
                              typeToggles: allTypesOff,
                            };
                          })
                        }
                      />
                    }
                    label="Notifications enabled"
                  />

                  <Box data-testid="notif-prefs-quiet-enabled">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={draftPreferences.quietHours.enabled}
                          onChange={(e) =>
                            updateDraft((prev) => ({
                              ...prev,
                              quietHours: { ...prev.quietHours, enabled: e.target.checked },
                            }))
                          }
                        />
                      }
                      label="Quiet hours"
                    />
                  </Box>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                      size="small"
                      type="time"
                      label="Quiet start"
                      value={draftPreferences.quietHours.start || ''}
                      onChange={(e) =>
                        updateDraft((prev) => ({
                          ...prev,
                          quietHours: { ...prev.quietHours, start: e.target.value },
                        }))
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      size="small"
                      type="time"
                      label="Quiet end"
                      value={draftPreferences.quietHours.end || ''}
                      onChange={(e) =>
                        updateDraft((prev) => ({
                          ...prev,
                          quietHours: { ...prev.quietHours, end: e.target.value },
                        }))
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      size="small"
                      label="Timezone"
                      value={draftPreferences.quietHours.timezone || ''}
                      onChange={(e) =>
                        updateDraft((prev) => ({
                          ...prev,
                          quietHours: { ...prev.quietHours, timezone: e.target.value },
                        }))
                      }
                    />
                  </Stack>

                  <Typography variant="subtitle2">Type Toggles</Typography>
                  {!draftPreferences.enabled ? (
                    <Typography variant="body2" color="text.secondary">
                      Enable notifications to configure types.
                    </Typography>
                  ) : null}
                  {Object.keys(draftPreferences.typeToggles).map((typeKey) => (
                    <Box key={typeKey} data-testid={`notif-prefs-type-toggle-${typeKey}`}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(draftPreferences.typeToggles[typeKey as NotificationType])}
                            disabled={!draftPreferences.enabled}
                            onChange={(e) =>
                              updateDraft((prev) => ({
                                ...prev,
                                typeToggles: {
                                  ...prev.typeToggles,
                                  [typeKey]: e.target.checked,
                                },
                              }))
                            }
                          />
                        }
                        label={formatTypeLabel(typeKey)}
                      />
                    </Box>
                  ))}

                  <Typography variant="subtitle2">Delivery Channels</Typography>
                  <Box data-testid="notif-prefs-delivery-email-toggle">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(draftPreferences.delivery?.email?.enabled)}
                          disabled={prefsSaving}
                          onChange={(e) => saveEmailDeliveryPreference(e.target.checked)}
                        />
                      }
                      label="Email notifications"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Email delivery is optional. In development, sending may be simulated unless enabled by an admin.
                  </Typography>

                  <Typography variant="subtitle2">Unusual Spend</Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={draftPreferences.category.unusualSpend.enabled}
                        onChange={(e) =>
                          updateDraft((prev) => ({
                            ...prev,
                            category: {
                              ...prev.category,
                              unusualSpend: {
                                ...prev.category.unusualSpend,
                                enabled: e.target.checked,
                              },
                            },
                          }))
                        }
                      />
                    }
                    label="Enabled"
                  />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                      size="small"
                      label="Lookback months"
                      type="number"
                      value={draftPreferences.category.unusualSpend.lookbackMonths ?? ''}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        if (!Number.isFinite(value)) return;
                        updateDraft((prev) => ({
                          ...prev,
                          category: {
                            ...prev.category,
                            unusualSpend: {
                              ...prev.category.unusualSpend,
                              lookbackMonths: value,
                            },
                          },
                        }));
                      }}
                    />
                    <TextField
                      size="small"
                      label="Multiplier"
                      type="number"
                      value={draftPreferences.category.unusualSpend.multiplier ?? ''}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        if (!Number.isFinite(value)) return;
                        updateDraft((prev) => ({
                          ...prev,
                          category: {
                            ...prev.category,
                            unusualSpend: {
                              ...prev.category.unusualSpend,
                              multiplier: value,
                            },
                          },
                        }));
                      }}
                    />
                    <TextField
                      size="small"
                      label="Min floor"
                      type="number"
                      value={draftPreferences.category.unusualSpend.minFloor ?? ''}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        if (!Number.isFinite(value)) return;
                        updateDraft((prev) => ({
                          ...prev,
                          category: {
                            ...prev.category,
                            unusualSpend: {
                              ...prev.category.unusualSpend,
                              minFloor: value,
                            },
                          },
                        }));
                      }}
                    />
                  </Stack>

                  <TextField
                    size="small"
                    label="Disabled categories (comma-separated)"
                    value={disabledCategoriesInput}
                    onChange={(e) => {
                      setDisabledCategoriesInput(e.target.value);
                      setPrefsDirty(true);
                    }}
                  />

                  <Typography variant="subtitle2">Credit card utilization alerts</Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                      size="small"
                      type="number"
                      label="Warn at (%)"
                      value={draftPreferences.account.creditUtilizationWarnPct ?? ''}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        if (!Number.isFinite(value)) return;
                        updateDraft((prev) => ({
                          ...prev,
                          account: {
                            ...prev.account,
                            creditUtilizationWarnPct: value,
                          },
                        }));
                      }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="Critical at (%)"
                      value={draftPreferences.account.creditUtilizationCriticalPct ?? ''}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        if (!Number.isFinite(value)) return;
                        updateDraft((prev) => ({
                          ...prev,
                          account: {
                            ...prev.account,
                            creditUtilizationCriticalPct: value,
                          },
                        }));
                      }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Applied to all credit-card accounts for current utilization alerts.
                  </Typography>

                  <Typography variant="subtitle2">Accounts</Typography>
                  {accounts.map((account) => {
                    const disabled = (draftPreferences.account.disabledAccountIds || []).includes(account._id);
                    const thresholdEntry = (draftPreferences.account.lowBalanceThresholds || []).find(
                      (item) => item.accountId === account._id
                    );
                    return (
                      <Paper key={account._id} variant="outlined" sx={{ p: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {account.name}
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={disabled}
                              onChange={(e) => toggleAccountDisabled(account._id, e.target.checked)}
                            />
                          }
                          label="Disable notifications for account"
                        />
                        {account.accountType === 'checking' ? (
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={thresholdEntry?.enabled || false}
                                  onChange={(e) =>
                                    toggleAccountThresholdEnabled(account._id, e.target.checked)
                                  }
                                />
                              }
                              label="Low balance threshold"
                            />
                            <TextField
                              size="small"
                              type="number"
                              label="Threshold"
                              value={thresholdEntry?.threshold ?? ''}
                              onChange={(e) => updateAccountThresholdValue(account._id, e.target.value)}
                            />
                          </Stack>
                        ) : null}
                      </Paper>
                    );
                  })}

                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={savePreferences}
                      disabled={!prefsDirty || prefsSaving}
                      data-testid="notif-prefs-save"
                    >
                      Save
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={cancelPreferences}
                      disabled={!prefsDirty || prefsSaving}
                      data-testid="notif-prefs-cancel"
                    >
                      Cancel
                    </Button>
                  </Stack>
                  {prefsSaveError ? (
                    <Typography variant="body2" color="error.main">
                      {prefsSaveError}
                    </Typography>
                  ) : null}
                  {prefsSaveSuccess ? (
                    <Typography variant="body2" color="success.main">
                      {prefsSaveSuccess}
                    </Typography>
                  ) : null}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Expand to load preferences.
                </Typography>
              )}
            </Paper>
          </Collapse>
        </Box>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : displayedNotifications.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {isPreview ? 'No unread notifications.' : fullFilter === 'all' ? 'No notifications.' : 'No unread notifications.'}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {displayedNotifications.map((n) => {
            const factsEntries = Object.entries(n.explain?.facts || {});
            const hasExplainability = Boolean(n.explain?.reason) || factsEntries.length > 0;
            const whyExpanded = expandedWhyIds[n._id] === true;
            return (
              <Paper key={n._id} variant="outlined" sx={{ p: 1.5 }} data-testid="notif-item">
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                  <Typography variant="subtitle2">{n.title}</Typography>
                  {n.severity ? (
                    <Chip size="small" label={formatTypeLabel(n.severity)} data-testid="notif-severity" />
                  ) : null}
                  {n.readAt ? <Chip size="small" label="Read" /> : null}
                </Stack>

                <Typography variant="body2" color="text.secondary">
                  {n.message}
                </Typography>

                <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
                  {n.entity?.date ? (
                    <Chip
                      size="small"
                      label={`Date: ${formatNotificationDate(n.entity.date)}`}
                      data-testid="notif-entity-chip"
                    />
                  ) : null}
                  {n.entity?.category ? (
                    <Chip size="small" label={`Category: ${n.entity.category}`} data-testid="notif-entity-chip" />
                  ) : null}
                  {n.entity?.accountId ? (
                    <Chip
                      size="small"
                      label={`Account: ${abbreviateId(n.entity.accountId)}`}
                      data-testid="notif-entity-chip"
                    />
                  ) : null}
                  {n.entity?.ledgerTransactionId ? (
                    <Chip
                      size="small"
                      label={`Tx: ${abbreviateId(n.entity.ledgerTransactionId)}`}
                      data-testid="notif-entity-chip"
                    />
                  ) : null}
                </Stack>

                {hasExplainability ? (
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      onClick={() =>
                        setExpandedWhyIds((prev) => ({
                          ...prev,
                          [n._id]: !prev[n._id],
                        }))
                      }
                      data-testid="notif-why-toggle"
                    >
                      Why?
                    </Button>

                    <Collapse in={whyExpanded}>
                      <Box sx={{ pt: 0.5 }} data-testid="notif-why-content">
                        {n.explain?.reason ? (
                          <Typography variant="body2" fontWeight={600}>
                            {n.explain.reason}
                          </Typography>
                        ) : null}
                        {factsEntries.map(([key, value]) => (
                          <Typography key={key} variant="body2" color="text.secondary">
                            {formatTypeLabel(key)}:{' '}
                            {typeof value === 'string' && /date/i.test(key)
                              ? formatNotificationDate(value)
                              : formatFactValue(value)}
                          </Typography>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                ) : null}

                {!n.readAt ? (
                  <Button size="small" sx={{ mt: 1 }} onClick={() => markRead(n._id)}>
                    Mark Read
                  </Button>
                ) : null}
              </Paper>
            );
          })}

          {hasHiddenPreviewItems ? (
            <Button component={RouterLink} to="/notifications" size="small" variant="outlined">
              View all
            </Button>
          ) : null}
        </Stack>
      )}
    </Paper>
  );
};

export default NotificationsPanel;
